export type TimePeriod = 'last7' | 'last30' | 'last90' | 'thisMonth' | 'lastMonth' | 'custom'
export const METRICS_EARLIEST_DATE = new Date(2024, 4, 1)
export const COLOR_OPS_NOTES = '#7B61FF'
export const COLOR_DRIVER_CANCELLATIONS = '#E5484D'
export const COLOR_NO_INTERVENTION = '#30A46C'
export const COLOR_WAIT_ADDED = '#7B61FF'
export const COLOR_HANDBALL_ADDED = '#E5484D'
export const COLOR_MANUAL_POC = '#F5A524'
export const COLOR_MANUAL_POD = '#0E9F6E'
export const TIME_PERIODS: { value: TimePeriod; label: string }[] = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom' },
]

export const EXCEPTION_METRICS_INFO = [
  'Tracks how often jobs require manual intervention after booking, including operations ' +
    'notes, driver cancellations after allocation, manually added charges, and proof of ' +
    "collection or delivery recorded by operations on the driver's behalf. It also shows " +
    'the proportion of jobs completed without any intervention.',
  'An operations note is a task added to the booking by a person, or a Note, Call or Meeting ' +
    'recorded through Log Activity. Tasks the system raises on its own are not counted.',
  'Each job is assigned to its collection date. Jobs without a known vehicle type are ' +
    'included under All vehicle types but excluded from both HGV and Non-HGV.',
  'Each headline shows the matching jobs out of all jobs completed in the selected period, ' +
    'and the percentage is that share rather than an average of the daily percentages.',
  'Figures are refreshed twice daily, so recent activity may take up to approximately ' +
    '12 hours to appear.',
].join('\n\n')

export const EXCEPTION_METRICS_FOOTNOTES = [
  'Each job is assigned to its collection date and counted once per chart. ' +
    'Cancelled jobs, quote-lost jobs, and scheduled templates are excluded. Jobs ' +
    'without a known vehicle type are included under All vehicle types but ' +
    'excluded from both HGV and Non-HGV, so the two filtered totals may not add ' +
    'up to the All total.',
  'Operations notes are tasks added by a person, or Notes, Calls and Meetings recorded ' +
    'through Log Activity. Tasks the system raises on its own, such as the request to rate ' +
    'the supplier, are not counted. A job can appear in more than one chart — for example, ' +
    'a job with both an operations note and a manually added charge will be counted in ' +
    'each relevant chart.',
  'Headline figures show the matching jobs out of all jobs completed in the selected ' +
    'period, and the percentage is that share rather than an average of the daily ' +
    'percentages. Figures are refreshed twice daily, so recent activity may take up to ' +
    'approximately 12 hours to appear.',
]

const SUMMARY_FIGURES_INFO =
  'The headline shows the matching jobs out of all jobs completed in the selected period, ' +
  'and the percentage is that share. It is not an average of the daily percentages.'

export const OPERATIONS_NOTES_INFO = [
  'Shows the number of jobs each day with at least one operations note — a task added to the ' +
    'booking by a person, or a Note, Call or Meeting recorded through Log Activity. Tasks the ' +
    'system raises on its own, such as the request to rate the supplier, are not counted.',
  'Each job is assigned to its collection date, and the daily percentage represents the share ' +
    'of jobs collected that day that had at least one operations note.',
  SUMMARY_FIGURES_INFO,
].join('\n\n')

export const DRIVER_CANCELLATIONS_INFO = [
  'Shows the number of jobs each day where an allocated driver was later deallocated or the ' +
    'allocation was changed — meaning the original allocation did not remain in place.',
  'Each job is assigned to its collection date, and the daily percentage represents the share ' +
    'of jobs collected that day that had an allocation change.',
  SUMMARY_FIGURES_INFO,
].join('\n\n')

export const JOBS_WITHOUT_INTERVENTION_INFO = [
  'Shows the number of jobs each day that ran without manual intervention — no operations ' +
    'notes, manually added charges, or proof of collection or delivery recorded by ' +
    "operations on the driver's behalf.",
  'Operations notes follow the same rule as the Jobs with operations notes chart: a task ' +
    'added to the booking by a person, or a Note, Call or Meeting recorded through Log ' +
    'Activity. Tasks the system raises on its own, such as the request to rate the supplier, ' +
    'do not count as intervention, so a job carrying only those tasks is still included here.',
  'Each job is assigned to its collection date, and the daily percentage represents the share ' +
    'of jobs collected that day that required no intervention.',
  SUMMARY_FIGURES_INFO,
].join('\n\n')

export const MANUAL_INTERVENTIONS_INFO = [
  'Shows the number of jobs each day where operations intervened after booking, grouped into ' +
    'four types: Wait Time Added, Handball Added, POC Added, and POD Added.',
  'Wait Time and Handball count jobs where the charge was added manually rather than ' +
    'automatically. POC and POD count jobs where operations recorded proof of collection or ' +
    "delivery on the driver's behalf. A job can appear in more than one category.",
].join('\n\n')
